import { Injectable } from '@angular/core';
import { AnalyticsHelper } from './analytics-config';

export interface AnalyticsEventParams {
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private eventQueue: Array<{ eventName: string; params: AnalyticsEventParams }> = [];
  private isGtagReady = false;

  constructor() {
    // Check if gtag is already loaded
    this.checkGtagReady();
  }

  private checkGtagReady(): void {
    const w = window as any;
    if (typeof w.gtag === 'function') {
      this.isGtagReady = true;
      this.flushQueue();
    } else {
      // Retry after 100ms
      setTimeout(() => this.checkGtagReady(), 100);
    }
  }

  /**
   * Track a custom event
   * @param eventName - Event name (use AnalyticsEvents constants)
   * @param params - Event parameters (use AnalyticsParams constants)
   */
  trackEvent(eventName: string, params?: AnalyticsEventParams): void {
    try {
      // Sanitize parameters
      const sanitizedParams = params ? AnalyticsHelper.sanitizeParams(params) : {};

      const w = window as any;
      if (typeof w.gtag === 'function') {
        // ALWAYS log for debugging (remove this after testing)
        // console.log('📊 [Analytics] Tracking event:', eventName, sanitizedParams);
        
        w.gtag('event', eventName, sanitizedParams);
        this.isGtagReady = true;
      } else {
        // Queue event if gtag not ready yet
        this.eventQueue.push({ eventName, params: sanitizedParams });
      }
    } catch (e) {
      console.error('[Analytics] Error tracking event:', eventName, e);
    }
  }

  /**
   * Flush queued events when gtag becomes ready
   */
  private flushQueue(): void {
    if (this.eventQueue.length === 0) return;
    
    const w = window as any;
    this.eventQueue.forEach(({ eventName, params }) => {
      try {
        w.gtag('event', eventName, params);
      } catch (e) {
        console.error('[Analytics] Error flushing event:', eventName, e);
      }
    });

    this.eventQueue = [];
  }

  /**
   * Set user properties
   * @param properties - User properties (e.g., user_id, account_id, role)
   */
  setUserProperties(properties: Record<string, any>): void {
    try {
      const w = window as any;
      if (typeof w.gtag === 'function') {
        w.gtag('set', 'user_properties', properties);
        if (!(window as any).environment?.production) {
          //console.log('[Analytics] User properties set:', properties);
        }
      }
    } catch (e) {
      console.error('[Analytics] Error setting user properties:', e);
    }
  }

  /**
   * Set user ID for cross-device tracking
   * @param userId - Unique user identifier
   */
  setUserId(userId: string): void {
    try {
      const w = window as any;
      if (typeof w.gtag === 'function') {
        w.gtag('set', { user_id: userId });
        if (!(window as any).environment?.production) {
          // console.log('[Analytics] User ID set:', userId);
        }
      }
    } catch (e) {
      console.error('[Analytics] Error setting user ID:', e);
    }
  }

  /**
   * Track page view (usually automatic, but can be called manually for SPAs)
   * @param pagePath - Page path (e.g., '/datapoints')
   * @param pageTitle - Page title
   */
  trackPageView(pagePath: string, pageTitle?: string): void {
    try {
      const w = window as any;
      if (typeof w.gtag === 'function') {
        w.gtag('event', 'page_view', {
          page_path: pagePath,
          page_title: pageTitle || document.title
        });
      }
    } catch (e) {
      console.error('[Analytics] Error tracking page view:', e);
    }
  }
}
